import { useSignal } from '@preact/signals'
import { createRef } from 'preact'
import { useDeliveryDates } from '@/composables/useDeliveryDates'
import { formatDate } from '@/utils/formatDate'
import { postcodeToState } from '@/components/utils/postcodeToState'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function HomeBannerPostcodeExperiment() {
  const postcode = useSignal<string | undefined>()
  const state = useSignal<string | undefined>()
  const loading = useSignal<boolean>(false)
  const defaultDeliveryDate = useSignal<string>('')
  const unServiceableArea = useSignal<boolean>(false)
  const input = createRef<HTMLInputElement>()
  const useDelivery = useDeliveryDates()

  const handleInput = (event: Event): void => {
    if (event.target instanceof HTMLInputElement) {
      postcode.value = event.target.value
    }
  }

  const handleSubmit = async (event: Event): Promise<void> => {
    event.preventDefault()

    if (!postcode.value) {
      if (input.current?.value) {
        postcode.value = input.current.value
      } else {
        return
      }
    }

    loading.value = true

    unServiceableArea.value = false
    state.value = ''

    const dates = await useDelivery.loadDeliveryDates(postcode.value)

    if (!dates) {
      unServiceableArea.value = true
      return
    }

    const availableDates = useDelivery.getAvailableDeliveryDates(dates)
    const deliveryDate = useDelivery.getDefaultDeliveryDate(availableDates)

    if (!deliveryDate) {
      unServiceableArea.value = true
      return
    }

    defaultDeliveryDate.value = formatDate(deliveryDate.date).replace(' ', ', ')
    state.value = postcodeToState(postcode.value)

    loading.value = false
  }

  const onChangePostcode = (): void => {
    defaultDeliveryDate.value = ''
    state.value = ''
    unServiceableArea.value = false
  }

  const onGetMyPlan = (): void => {
    const url = new URL('/get-started', window.location.origin)

    if (postcode.value) {
      url.searchParams.set('postcode', postcode.value)
    }

    location.href = url.toString()
  }

  return (
    <div class="tw-w-auto tw-flex tw-justify-center md:tw-justify-start tw-relative tw-z-20 tw-transition-opacity tw-opacity-100 tw-min-h-40">
      <div class="sm:tw-max-w-lg tw-w-full">
        <div class="tw-bg-mint-green tw-p-6 tw-relative tw-z-50 sm:tw-rounded-2xl lg:tw-px-8 tw-h-full">
          <div className={defaultDeliveryDate.value === '' ? 'tw-hidden' : 'tw-flex tw-gap-3 tw-items-center tw-mb-4'}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="24" height="24" rx="12" fill="#10B193"></rect>
              <path
                d="M17.742 7.97a.857.857 0 0 1 0 1.212l-6.849 6.848a.857.857 0 0 1-1.212 0l-3.424-3.424a.857.857 0 0 1 1.212-1.212l2.82 2.817 6.243-6.241a.857.857 0 0 1 1.212 0h-.002Z"
                fill="#FCFBF9"
              ></path>
            </svg>
            <div>
              <div class="tw-font-bold">{defaultDeliveryDate.value}</div>
              <div>
                Earliest delivery to&nbsp;
                <div onClick={onChangePostcode} class="tw-cursor-pointer tw-inline tw-font-bold tw-underline">
                  {postcode.value} {state.value}
                  <svg
                    class="tw-inline tw-ml-3 tw-mb-[6px]"
                    width="20"
                    height="20"
                    fill="none"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="m8.333 4.3749c0.34515 0 0.62495 0.27986 0.625 0.625 0 0.34518-0.27982 0.625-0.625 0.625h-3.958c-0.69033 0-1.25 0.55968-1.25 1.25v8.75c0 0.6903 0.55964 1.25 1.25 1.25h8.75c0.6904 0 1.25-0.5597 1.25-1.25v-3.958c0-0.3452 0.2798-0.625 0.625-0.625s0.625 0.2798 0.625 0.625v3.958c0 1.3807-1.1193 2.5-2.5 2.5h-8.75c-1.3807 0-2.5-1.1193-2.5-2.5v-8.75c5e-5 -1.3807 1.1193-2.5 2.5-2.5h3.958zm6.6826-2.4844c0.8543-0.85421 2.2395-0.85421 3.0938 0 0.8542 0.85426 0.8542 2.2395 0 3.0938l-8.8487 8.8486c-0.51395 0.5139-1.1481 0.8921-1.8447 1.0996l-2.2373 0.666c-0.21994 0.0655-0.45882 6e-3 -0.62109-0.1562-0.16225-0.1623-0.22176-0.4012-0.15625-0.6211l0.66601-2.2373c0.20753-0.6966 0.58568-1.3308 1.0996-1.8448l8.8486-8.8486zm-7.9648 9.7334c-0.36703 0.3671-0.63696 0.8199-0.78516 1.3174l-0.33691 1.1299 1.1299-0.3369c0.49761-0.1483 0.95123-0.4181 1.3184-0.7852l7-7.001-1.3252-1.3252-7.001 7.001zm10.175-8.8496c-0.3661-0.36605-0.9601-0.36605-1.3262 0l-0.9639 0.96485 1.3252 1.3252 0.9649-0.96387c0.3661-0.36612 0.3661-0.96007 0-1.3262z"
                      fill="#005648"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className={unServiceableArea.value ? 'tw-mb-4' : 'tw-hidden'}>
            <div class="tw-text-left tw-text-base md:tw-text-lg tw-pointer-events-none tw-font-bold">
              Sorry we don't deliver to your postcode yet.
            </div>
            <div class="tw-text-left tw-text-base md:tw-text-lg tw-pointer-events-none">
              Check back soon - we're growing quickly!
            </div>
          </div>

          <div className={defaultDeliveryDate.value === '' && !unServiceableArea.value ? 'tw-mb-4' : 'tw-hidden'}>
            <div class="tw-text-left tw-text-base md:tw-text-lg tw-pointer-events-none">
              Check your Lyka delivery date
            </div>
          </div>

          <form
            className={
              defaultDeliveryDate.value === '' && !unServiceableArea.value ? 'tw-relative tw-z-10' : 'tw-hidden'
            }
            onSubmit={handleSubmit}
          >
            <div class="tw-flex tw-bg-white tw-rounded-full tw-border tw-border-green-dark tw-justify-between tw-min-w-0">
              <input
                type="text"
                ref={input}
                maxlength={4}
                pattern="[0-9]{4}"
                name="postcode"
                class="tw-min-w-0 focus:tw-outline-none tw-pl-4 tw-rounded-l-full tw-text-base lg:tw-text-lg tw-grow tw-placeholder-green-dark/50"
                placeholder="Enter postcode"
                spellcheck={false}
                role="combobox"
                onInput={handleInput}
              />

              <div>
                <button type="submit" class="lyka-btn lyka-btn-md lyka-btn-dark">
                  Check delivery
                </button>
              </div>
            </div>
          </form>

          <button
            onClick={onGetMyPlan}
            className={
              defaultDeliveryDate.value === '' ? 'tw-hidden' : 'lyka-btn lyka-btn-md lyka-btn-dark tw-block tw-w-full'
            }
          >
            Get my plan
          </button>

          <button
            onClick={onChangePostcode}
            className={unServiceableArea.value ? 'lyka-btn lyka-btn-md lyka-btn-dark tw-block tw-w-full' : 'tw-hidden'}
          >
            Change postcode
          </button>
        </div>
      </div>
    </div>
  )
}
